import { GroupParticipant } from '@weave/schema-gen-ts/dist/schemas/sms/shared/v1/models.pb';
import { GroupCheckIO } from '../types';
import { UseSendV3MutationEndpointArgs, useSendV3Mutation } from './use-send-v3-mutation';

type GroupCheckRequestWithParticipants = Omit<GroupCheckIO['input'], 'recipients'> & {
  participants: GroupParticipant[];
};

const normalizeGroupCheckRequest = (request: GroupCheckRequestWithParticipants): GroupCheckIO['input'] => ({
  ...request,
  recipients: request.participants.map((participant) => ({
    // GroupParticipant is the read/display shape, GroupRecipient is the send-v3 wire contract.
    personPhone: participant.phoneNumber ?? '',
    personId: participant.person?.personId,
  })),
});

/**
 * Wraps the SendV3 GroupCheck endpoint with a participant-first API for UI call sites.
 *
 * Callers pass `participants: GroupParticipant[]` (the read/display shape used in inbox flows),
 * and this hook normalizes each participant into the wire contract expected by SendV3:
 * `recipients: [{ personPhone, personId }]`.
 *
 * The returned `mutate` and `mutateAsync` keep the standard mutation signatures while accepting
 * participant-based request objects, so call sites do not need to manually build recipient payloads.
 */
export const useGroupCheckMutation = <
  E = unknown,
  C = unknown,
  OtherOptions extends object = never,
  RequestOverride extends GroupCheckRequestWithParticipants = GroupCheckRequestWithParticipants
>({
  options,
  ...args
}: UseSendV3MutationEndpointArgs<'GroupCheck', E, C, OtherOptions, GroupCheckIO['input']> = {}) => {
  const mutation = useSendV3Mutation<'GroupCheck', E, C, OtherOptions, GroupCheckIO['input']>({
    endpointName: 'GroupCheck',
    ...args,
    options,
  });

  const handleMutate = (
    request: RequestOverride,
    ...rest: Parameters<typeof mutation.mutate> extends [any, ...infer R] ? R : never
  ) => {
    const normalizedRequest = normalizeGroupCheckRequest(request as GroupCheckRequestWithParticipants);
    mutation.mutate(normalizedRequest, ...rest);
  };

  const handleMutateAsync: (
    request: RequestOverride,
    ...rest: Parameters<typeof mutation.mutateAsync> extends [any, ...infer R] ? R : never
  ) => Promise<Awaited<ReturnType<typeof mutation.mutateAsync>>> = (request, ...rest) => {
    const normalizedRequest = normalizeGroupCheckRequest(request as GroupCheckRequestWithParticipants);
    return mutation.mutateAsync(normalizedRequest, ...rest);
  };

  return {
    ...mutation,
    mutate: handleMutate,
    mutateAsync: handleMutateAsync,
  };
};
